import React, { memo } from 'react';
import { Tooltip, Typography, Divider, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { diagnosticsClassColor } from '../../../../../../helpers/manageColors';
import { useStyles } from '../DiagnosticRevisionTable.style';
import { getMatchedProj, quadrantMap, rightPathMap, leftPathMap } from '../constants/constants';

const lesionQuadrant = (breast, location, depth, color) => {
  // Ensure breast is a string - handle array projections
  const breastString = Array.isArray(breast) ? breast[0] || '' : typeof breast === 'string' ? breast : '';

  const quadrant = quadrantMap[location] || '-';
  const pathMap =
    breastString?.toLowerCase() === 'right' || breastString === 'rcc' || breastString === 'rmlo'
      ? rightPathMap
      : leftPathMap;
  const path = pathMap[quadrant];

  if (quadrant === '-' || !breastString || !location) {
    if (!location && breastString && color) {
      const fullBreastPath =
        breastString?.toLowerCase() === 'right'
          ? 'M100,100 L100,10 A90,90 0 0,1 190,100 Z M100,100 L190,100 A90,90 0 0,1 100,190 Z M100,100 L100,10 A90,90 0 0,0 10,100 Z M100,100 L10,100 A90,90 0 0,0 100,190 Z'
          : 'M100,100 L100,10 A90,90 0 0,0 10,100 Z M100,100 L10,100 A90,90 0 0,0 100,190 Z M100,100 L100,10 A90,90 0 0,1 190,100 Z M100,100 L190,100 A90,90 0 0,1 100,190 Z';

      return (
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
          }}>
          <svg width='30' height='30' viewBox='0 0 200 200'>
            <circle cx='100' cy='100' r='90' stroke='#666' strokeWidth='10' fill='none' />
            <path d={fullBreastPath} fill={color} />
            <line x1='100' y1='10' x2='100' y2='190' stroke='#afafaf' strokeWidth='4' />
            <line x1='10' y1='100' x2='190' y2='100' stroke='#afafaf' strokeWidth='4' />
          </svg>
        </div>
      );
    }

    return (
      <div
        style={{
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
        }}>
        <svg width='30' height='30' viewBox='0 0 200 200'>
          <circle
            cx='100'
            cy='100'
            r='90'
            stroke='#666'
            strokeWidth='10'
            fill={breastString && !location ? (color ? color : 'rgba(67, 93, 100, 0.78)') : 'none'}
          />
          <line x1='100' y1='10' x2='100' y2='190' stroke='#666' strokeWidth='5' />
          <line x1='10' y1='100' x2='190' y2='100' stroke='#666' strokeWidth='5' />
        </svg>
      </div>
    );
  }

  return (
    <div
      style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
      <svg width='30' height='30' viewBox='0 0 200 200'>
        <circle cx='100' cy='100' r='90' stroke='#666' strokeWidth='10' fill='none' />
        <path d={path} fill={color || 'white'} />
        <line x1='100' y1='10' x2='100' y2='190' stroke='#666' strokeWidth='5' />
        <line x1='10' y1='100' x2='190' y2='100' stroke='#666' strokeWidth='5' />
      </svg>
    </div>
  );
};

const emptyLesionQuadrant = () => {
  return (
    <div
      style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
      <svg width='30' height='30' viewBox='0 0 200 200'>
        <circle cx='100' cy='100' r='90' stroke='#666' strokeWidth='10' fill='none' />
        <line x1='100' y1='10' x2='100' y2='190' stroke='#666' strokeWidth='5' />
        <line x1='10' y1='100' x2='190' y2='100' stroke='#666' strokeWidth='5' />
      </svg>
    </div>
  );
};

const QuadrantComponent = ({
  lesionAllInfo,
  projection,
  finding_class,
  isExpanded,
  isClustered,
  matched,
  smallScreen,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <>
      {isExpanded && matched ? (
        <div className={classes.empty_cell_container} />
      ) : isClustered ? (
        <div className={classes.empty_cell_container}>-</div>
      ) : (
        <div className={classes.location_container}>
          <Typography
            className={`${classes.breast_text} ${classes.breast_text_left}`}
            style={{ top: smallScreen ? '-8px' : '0' }}>
            R
          </Typography>
          {projection === 'rcc' || projection === 'rmlo' ? (
            <Tooltip
              arrow
              title={
                <>
                  <Typography className={classes.tooltip_text}>
                    {!!matched
                      ? `${projection?.toUpperCase()} / ${getMatchedProj(projection)?.toUpperCase()}`
                      : projection?.toUpperCase()}
                  </Typography>
                  {(lesionAllInfo?.location || lesionAllInfo?.depth) && (
                    <>
                      <Divider style={{ margin: '4px 0' }} />
                      <Typography className={classes.tooltip_text}>
                        {lesionAllInfo?.location && t(`diagnose.${lesionAllInfo?.location}`)}{' '}
                        {lesionAllInfo?.depth && t(`diagnose.${lesionAllInfo?.depth}`)}
                      </Typography>
                    </>
                  )}
                </>
              }>
              {lesionQuadrant(
                projection,
                lesionAllInfo?.location,
                lesionAllInfo?.depth,
                `${diagnosticsClassColor(finding_class, theme)}`
              )}
            </Tooltip>
          ) : (
            emptyLesionQuadrant()
          )}
          {projection === 'lcc' || projection === 'lmlo' ? (
            <Tooltip
              arrow
              title={
                <>
                  <Typography className={classes.tooltip_text}>
                    {!!matched
                      ? `${projection?.toUpperCase()} / ${getMatchedProj(projection)?.toUpperCase()}`
                      : projection?.toUpperCase()}
                  </Typography>
                  {(lesionAllInfo?.location || lesionAllInfo?.depth) && (
                    <>
                      <Divider style={{ margin: '4px 0' }} />
                      <Typography className={classes.tooltip_text}>
                        {lesionAllInfo?.location && t(`diagnose.${lesionAllInfo?.location}`)}{' '}
                        {lesionAllInfo?.depth && t(`diagnose.${lesionAllInfo?.depth}`)}
                      </Typography>
                    </>
                  )}
                </>
              }>
              {lesionQuadrant(
                projection,
                lesionAllInfo?.location,
                lesionAllInfo?.depth,
                `${diagnosticsClassColor(finding_class, theme)}`
              )}
            </Tooltip>
          ) : (
            emptyLesionQuadrant()
          )}
          <Typography
            className={`${classes.breast_text} ${classes.breast_text_right}`}
            style={{ top: smallScreen ? '-8px' : '0' }}>
            L
          </Typography>
        </div>
      )}
    </>
  );
};

export const Quadrant = memo(QuadrantComponent);
